import type {
    EndpointContract,
    EndpointContractFilterDescriptor,
} from '@/types/endpoint-contract'
import {unpackEntityLinkId} from '@/lib/entity-link-packing'

// URL param names — technical identifiers, never localised.
const DEFAULT_Q_PARAM = 'q'

/**
 * Maps a filter descriptor's `kind` (and optional `values` / `element`) to the
 * TypeScript value type accepted by {@link buildFilterQuery}. An `undefined`
 * value is always allowed and signals "skip this filter".
 */
// `enum` descriptors carry `values` for backend parity and runtime validation,
// but the TS input stays `string | undefined` — callers typically pass URL
// params or component state that are already-loose strings, and tightening to
// the literal union would force a cast at every call site without catching
// any real bugs (the backend still validates the enum).
type ValueForKind<TDescriptor extends EndpointContractFilterDescriptor> =
    TDescriptor['kind'] extends 'csv'
        ? ReadonlyArray<string> | string | undefined
        : TDescriptor['kind'] extends 'boolean'
            ? string | boolean | undefined
            : string | undefined

/**
 * Derived type for the second argument of {@link buildFilterQuery}. Every key
 * must match an entry in the endpoint's filter map, and each value is narrowed
 * to the TypeScript type implied by that filter's declared kind.
 */
export type FiltersFor<TEndpoint extends EndpointContract> = {
    [K in keyof TEndpoint['filter']]?:
        TEndpoint['filter'][K] extends EndpointContractFilterDescriptor
            ? ValueForKind<TEndpoint['filter'][K]>
            : never
}

/**
 * Options controlling how {@link buildFilterQuery} emits the URL string.
 * `q` is the free-text search value (Unified Search §5.6).
 * `attrPrefix` is written before dynamic-attribute filter names (§4.10.2):
 * with `attrPrefix: true` a filter named `costCenter` is emitted as
 * `attr.costCenter=...`. Static filters declared on the contract never get
 * the prefix — only the dynamic overrides in {@link dynamicAttributes} do.
 */
export interface BuildFilterQueryOptions {
    readonly q?: string
    readonly qParam?: string
    /**
     * Extra attribute-filter values keyed by attribute definition name. Written
     * to the URL as `attr.{name}=value` (§4.10.2 attribute filter encoding).
     * Array values are CSV-joined.
     */
    readonly dynamicAttributes?: Readonly<Record<string, string | ReadonlyArray<string> | undefined>>
    /**
     * URL namespace prefix (§5.7). When set, every param name is prefixed with
     * `{namespace}.` — e.g., `accounts.q`, `accounts.status`.
     */
    readonly namespace?: string
}

function stringifyValue(
    descriptor: EndpointContractFilterDescriptor,
    value: unknown,
): string | undefined {
    if (value === null || value === undefined) {
        return undefined
    }
    if (typeof value === 'string') {
        return mapElement(descriptor, value)
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value)
    }
    if (Array.isArray(value)) {
        if (descriptor.kind !== 'csv') {
            return undefined
        }
        const filtered = value
            .filter((v): v is string => v !== null && v !== undefined && v !== '')
            .map((v) => mapElement(descriptor, String(v)))
        return filtered.length === 0 ? undefined : filtered.join(',')
    }
    return undefined
}

/**
 * UUID-kind filters (single `uuid` and `csv`+`element: 'uuid'`) carry
 * pipe-packed `id|displayName|entityType|subType` values in URL state so
 * the picker chip can re-render after a refresh. Strip back to id before
 * the HTTP call so the backend `UUID` / `List<UUID>` parameter binders
 * don't choke on the trailing parts.
 *
 * For multi-select `csv`+`uuid`, callers pass either a `string[]` (each
 * element packed) or a single string of comma-joined packed values; this
 * helper handles one element at a time, leaving the join to the caller.
 */
function mapElement(descriptor: EndpointContractFilterDescriptor, raw: string): string {
    if (descriptor.kind === 'uuid') {
        return unpackEntityLinkId(raw)
    }
    if (descriptor.kind === 'csv' && descriptor.element === 'uuid') {
        return raw.split(',')
            .map((part) => unpackEntityLinkId(part))
            .join(',')
    }
    return raw
}

function withNamespace(name: string, namespace?: string): string {
    return namespace ? `${namespace}.${name}` : name
}

/**
 * Builds a URL-encoded filter query string for an endpoint contract. Keys and
 * value kinds are narrowed at the type level against `endpoint.filter`;
 * undefined or empty-string values are skipped; the emit order matches
 * `endpoint.filter` iteration order (stable across calls).
 *
 * An empty result is returned as `''` (not `'?'`), matching URLSearchParams.
 */
export function buildFilterQuery<TEndpoint extends EndpointContract>(
    endpoint: TEndpoint,
    filters: FiltersFor<TEndpoint>,
    options: BuildFilterQueryOptions = {},
): string {
    const params = new URLSearchParams()
    const namespace = options.namespace ?? endpoint.namespace
    const qParam = options.qParam ?? endpoint.search?.param ?? DEFAULT_Q_PARAM

    if (options.q !== undefined && options.q !== null && options.q !== '') {
        params.append(withNamespace(qParam, namespace), options.q)
    }

    const filterMap = endpoint.filter as Record<string, EndpointContractFilterDescriptor>
    const filterInput = filters as Record<string, unknown>
    for (const key of Object.keys(filterMap)) {
        if (!(key in filterInput)) {
            continue
        }
        const descriptor = filterMap[key]
        const raw = filterInput[key]
        if (raw === undefined || raw === null || raw === '') {
            continue
        }
        const serialized = stringifyValue(descriptor, raw)
        if (serialized === undefined || serialized === '') {
            continue
        }
        params.append(withNamespace(descriptor.name, namespace), serialized)
    }

    if (options.dynamicAttributes) {
        for (const [attrName, value] of Object.entries(options.dynamicAttributes)) {
            if (value === undefined || value === null) continue
            const serialized = Array.isArray(value)
                ? value.filter(v => v !== undefined && v !== null && v !== '').join(',')
                : String(value)
            if (serialized === '') continue
            params.append(withNamespace(`attr.${attrName}`, namespace), serialized)
        }
    }

    return params.toString()
}

/**
 * Returns true if any filter value is meaningful. Use this to drive
 * `TableEmptyState`'s `hasFilters` prop so the empty-state messaging aligns
 * with what {@link buildFilterQuery} actually sends to the backend. Resolves
 * OQ-2 in the endpoint-contracts spec.
 *
 * Array filters count as "active" only when at least one element is
 * non-empty — an array of only null/undefined/empty-string values is
 * dropped by {@link buildFilterQuery} and must be treated the same way
 * here. Otherwise the UI would show a "filters applied" indicator while
 * the URL carried no filter params.
 */
export function hasActiveFilters(filters: Record<string, unknown>): boolean {
    return Object.values(filters).some((v) => {
        if (v == null || v === '') return false
        if (Array.isArray(v)) {
            return v.some((el) => el != null && el !== '')
        }
        return true
    })
}
